<script async setup lang="ts">
import {
  type Component,
  computed,
  inject,
  onErrorCaptured,
  provide,
  reactive,
  ref,
  shallowReactive,
  shallowReadonly,
  watch,
  watchEffect
} from "vue";
import { viewRepositoryKey } from "@/keys";
import { contextKey, useContext, ViewContext, ViewNavigateOptions } from "@hantera/portal-app";

const props = defineProps<{
  uri: string
}>();

const viewState = defineModel<Record<string, any>>('state', {
  default: {},
  required: false
});

const context = useContext()
const viewRepository = inject(viewRepositoryKey, {} as any)

const viewContext: ViewContext = shallowReactive({
  viewId: 'embedded',
  name: ref('embedded'),
  hasPendingChanges: ref(false),
  close() {
    // Embedded view can't be closed.
  },
  navigate(uri: string, options?: ViewNavigateOptions) {

    const [path, querystring] = uri.split('?')

    const query = options?.query ?? {}
    if (querystring) {
      const params = new URLSearchParams(querystring)
      params.forEach((val, key) => {
        query[key] = val
      })
    }

    viewContext.path = path
    viewContext.query = query
    viewContext.state = reactive(options?.state ?? {})
  },
} as ViewContext)

watchEffect(() => {

  const [path, querystring] = props.uri.split('?')

  const query: Record<string, string> = {}
  if (querystring) {
    const params = new URLSearchParams(querystring)
    params.forEach((val, key) => {
      query[key] = val
    })
  }

  viewContext.uri = props.uri
  viewContext.path = path
  viewContext.query = query
  viewContext.state = viewState.value

  viewContext.visible = context.currentView.value?.visible ?? true
  viewContext.active = context.currentView.value?.active ?? true
})

watch(viewContext.state, () => {
  viewState.value = viewContext.state
}, { deep: true })

provide(contextKey, {
  ...context,
  currentView: computed(() => shallowReadonly(viewContext)),
})

const state = computed(() => viewRepository.getView(context, viewContext.path))

const viewKey = computed(() => viewContext.uri)

const error = ref<{
  component: string
  message: string
  stack?: string
}>();

onErrorCaptured((err, instance) => {
  error.value = {
    component: instance?.$.type.__name ?? 'unknown',
    message: err.message,
    stack: err.stack
  };
  console.error(err)

  return false;
})
</script>

<template>

  <template v-if="state.state === 'notFound'">
    View not found ({{ viewContext.uri }})
  </template>
  <template v-else-if="state.state === 'unauthorized'">
    <div class="view-error-container">
      <div class="view-error box-content box-pop">
        <h2>Unable to display</h2>
        <p>
          You do not have permission to view this page
        </p>
      </div>
    </div>
  </template>
  <template v-else-if="error">
    <div class="view-error">
      <h2>Error in '{{ error.component }}' from view '{{ viewContext.uri }}'</h2>

      <p>
        {{ error.message }}
      </p>
      <pre><code v-if="error.stack">{{ error.stack }}</code></pre>
    </div>
  </template>
  <template v-else>
    <div class="view-container neat-scroll">
      <Suspense>
        <component :is="state.component!" :key="viewKey" v-bind="state.props" />
      </Suspense>
    </div>
  </template>


</template>

<style scoped>
.view-container {
  container-type: inline-size;
  position: relative;
  height: 100%;
  width: 100%;
  overflow: auto;
}

.view-error-container {
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.view-error {
  border-color: var(--color-error-50);
  padding: 10px;
  border-radius: 10px;
}

.view-error h2 {
  font-size: 16px;
  font-weight: bold;
  margin: 5px 0;
}
</style>